export const RUNTIME_CONFIG_URL = "/env.json";

export type RuntimeConfig = {
  VITE_SBA_BANK_API_URL: string;
  /** Transaction-reporting / MAS (cockpit-api). Other features keep bank-api URL. */
  VITE_COCKPIT_API_URL: string;
  VITE_ADMIN_APP_URL: string;
  VITE_AUTH_CLIENT_ID: string;
  VITE_AUTH_TENANT_ID: string;
  VITE_AUTH_BASE_URL: string;
  VITE_AUTH_AUTHORIZATION_URL: string;
  VITE_AUTH_COUNTRY_CODE?: string;
  VITE_SLEEK_BACK_API_URL?: string;
  VITE_GOOGLE_OAUTH_CLIENT_ID?: string;
};

let loaded: RuntimeConfig | null = null;

function trimString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function read(raw: Record<string, unknown>, key: keyof RuntimeConfig): string | undefined {
  return trimString(raw[key]);
}

function requireString(
  value: string | undefined,
  key: keyof RuntimeConfig,
  errors: string[]
): string {
  if (!value) {
    errors.push(key);
    return "";
  }
  return value;
}

export function parseRuntimeConfig(
  input: unknown,
  options: { strictAuthCredentials?: boolean } = {}
): RuntimeConfig {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new Error("Invalid runtime config: expected a JSON object");
  }
  const raw = input as Record<string, unknown>;
  const errors: string[] = [];
  const strictAuthCredentials = options.strictAuthCredentials !== false;

  const clientId = read(raw, "VITE_AUTH_CLIENT_ID") ?? "";
  const tenantId = read(raw, "VITE_AUTH_TENANT_ID") ?? "";

  const bankApiUrl = requireString(
    read(raw, "VITE_SBA_BANK_API_URL"),
    "VITE_SBA_BANK_API_URL",
    errors
  );
  // Optional until staging/prod cockpit-api cutover — fall back to bank-api.
  const cockpitApiUrl = read(raw, "VITE_COCKPIT_API_URL") ?? bankApiUrl;

  const config: RuntimeConfig = {
    VITE_SBA_BANK_API_URL: bankApiUrl,
    VITE_COCKPIT_API_URL: cockpitApiUrl,
    VITE_ADMIN_APP_URL: requireString(
      read(raw, "VITE_ADMIN_APP_URL"),
      "VITE_ADMIN_APP_URL",
      errors
    ),
    VITE_AUTH_CLIENT_ID: strictAuthCredentials
      ? requireString(clientId || undefined, "VITE_AUTH_CLIENT_ID", errors)
      : clientId,
    VITE_AUTH_TENANT_ID: strictAuthCredentials
      ? requireString(tenantId || undefined, "VITE_AUTH_TENANT_ID", errors)
      : tenantId,
    VITE_AUTH_BASE_URL: requireString(
      read(raw, "VITE_AUTH_BASE_URL"),
      "VITE_AUTH_BASE_URL",
      errors
    ),
    VITE_AUTH_AUTHORIZATION_URL: requireString(
      read(raw, "VITE_AUTH_AUTHORIZATION_URL"),
      "VITE_AUTH_AUTHORIZATION_URL",
      errors
    ),
    VITE_AUTH_COUNTRY_CODE: read(raw, "VITE_AUTH_COUNTRY_CODE"),
    VITE_SLEEK_BACK_API_URL: read(raw, "VITE_SLEEK_BACK_API_URL"),
    VITE_GOOGLE_OAUTH_CLIENT_ID: read(raw, "VITE_GOOGLE_OAUTH_CLIENT_ID"),
  };

  if (errors.length > 0) {
    throw new Error(`Invalid runtime config: missing ${[...new Set(errors)].join(", ")}`);
  }

  return config;
}

function fromViteEnv(env: ImportMetaEnv): RuntimeConfig {
  const payload: Record<string, unknown> = { ...env };
  if (!trimString(payload.VITE_SBA_BANK_API_URL)) {
    payload.VITE_SBA_BANK_API_URL = "http://localhost:4300";
  }
  if (!trimString(payload.VITE_COCKPIT_API_URL)) {
    payload.VITE_COCKPIT_API_URL = "http://localhost:4350";
  }
  if (!trimString(payload.VITE_ADMIN_APP_URL)) {
    payload.VITE_ADMIN_APP_URL = "http://localhost:5000";
  }
  if (!trimString(payload.VITE_AUTH_BASE_URL)) {
    payload.VITE_AUTH_BASE_URL = "https://auth-api-sit.sleek.com/";
  }
  if (!trimString(payload.VITE_AUTH_AUTHORIZATION_URL)) {
    payload.VITE_AUTH_AUTHORIZATION_URL = "https://auth-sit.sleek.com/app/authorize";
  }
  return parseRuntimeConfig(payload, { strictAuthCredentials: false });
}

function canUseViteFallback(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "test";
}

export function getRuntimeConfig(): RuntimeConfig {
  if (loaded) {
    return loaded;
  }
  if (canUseViteFallback()) {
    loaded = fromViteEnv(import.meta.env);
    return loaded;
  }
  throw new Error(
    "Runtime config is not loaded. Call loadRuntimeConfig() before starting the app."
  );
}

export function resetRuntimeConfig(): void {
  loaded = null;
}

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  if (loaded) {
    return loaded;
  }
  if (canUseViteFallback()) {
    loaded = fromViteEnv(import.meta.env);
    return loaded;
  }

  const response = await fetchImpl(RUNTIME_CONFIG_URL, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load ${RUNTIME_CONFIG_URL}: HTTP ${response.status}`);
  }
  const json: unknown = await response.json();
  loaded = parseRuntimeConfig(json);
  return loaded;
}

export function adminCompanyOverviewUrl(companyId: string | number): string {
  return `${getRuntimeConfig().VITE_ADMIN_APP_URL}/admin/company-overview/?cid=${companyId}`;
}
